import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { ArrowLeft, Plus, Edit, Trash2, Ruler, Users, Wrench, Package, BarChart3, Save, FolderOpen } from 'lucide-react';
import { Slider } from '../components/ui/slider';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { fmtDate } from '../lib/dateUtils';

const WORK_STATUSES = {
  da_iniziare: { l: 'Da Iniziare', c: 'bg-slate-100 text-slate-600' },
  in_corso: { l: 'In Corso', c: 'bg-blue-100 text-blue-700' },
  completata: { l: 'Completata', c: 'bg-green-100 text-green-700' },
  sospesa: { l: 'Sospesa', c: 'bg-amber-100 text-amber-700' }
};

export default function SitePlanningPage() {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const { isRole } = useAuth();
  const canEdit = isRole('admin', 'aspp', 'capo_cantiere', 'qhse');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWorkForm, setShowWorkForm] = useState(false);
  const [editWork, setEditWork] = useState(null);
  const [showMaterialForm, setShowMaterialForm] = useState(false);
  const [mqEdit, setMqEdit] = useState('');
  const [savingMq, setSavingMq] = useState(false);
  const [workForm, setWorkForm] = useState({
    name: '', description: '', mq_planned: '', mq_completed: '', status: 'da_iniziare', start_date: '', end_date: '', notes: ''
  });
  const [materialForm, setMaterialForm] = useState({ name: '', quantity: '', unit: 'pz', notes: '' });
  const [projects, setProjects] = useState([]);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [projectForm, setProjectForm] = useState({
    name: '', description: '', start_date: '', end_date: '', progress_pct: 0, responsible: '', budget: '', status: 'attivo', notes: ''
  });

  const load = async () => {
    try {
      const res = await api.get(`/site-planning/${siteId}`);
      setData(res.data);
      setMqEdit(res.data.mq_total?.toString() || '0');
      try {
        const projRes = await api.get('/projects/by-site/' + siteId);
        setProjects(projRes.data);
      } catch {}
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [siteId]);

  const handleSaveMq = async () => {
    setSavingMq(true);
    try {
      await api.put(`/site-planning/${siteId}/mq`, { mq_total: parseFloat(mqEdit) || 0 });
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
    finally { setSavingMq(false); }
  };

  const handleSaveWork = async () => {
    try {
      const payload = {
        ...workForm,
        mq_planned: parseFloat(workForm.mq_planned) || 0,
        mq_completed: parseFloat(workForm.mq_completed) || 0
      };
      if (editWork) {
        await api.put(`/site-planning/works/${editWork.id}`, payload);
      } else {
        await api.post(`/site-planning/${siteId}/works`, payload);
      }
      setShowWorkForm(false);
      setEditWork(null);
      setWorkForm({ name: '', description: '', mq_planned: '', mq_completed: '', status: 'da_iniziare', start_date: '', end_date: '', notes: '' });
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleDeleteWork = async (id) => {
    if (!window.confirm('Eliminare questa lavorazione?')) return;
    try { await api.delete(`/site-planning/works/${id}`); load(); } catch (err) { alert('Errore'); }
  };

  const handleAddMaterial = async () => {
    try {
      await api.post(`/site-planning/${siteId}/materials`, {
        ...materialForm, quantity: parseFloat(materialForm.quantity) || 0
      });
      setShowMaterialForm(false);
      setMaterialForm({ name: '', quantity: '', unit: 'pz', notes: '' });
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleSaveProject = async () => {
    try {
      const payload = {
        ...projectForm,
        site_id: siteId,
        progress_pct: parseInt(projectForm.progress_pct) || 0,
        budget: projectForm.budget ? parseFloat(projectForm.budget) : null
      };
      if (editProject) {
        await api.put('/projects/' + editProject.id, payload);
      } else {
        await api.post('/projects', payload);
      }
      setShowProjectForm(false);
      setEditProject(null);
      setProjectForm({ name: '', description: '', start_date: '', end_date: '', progress_pct: 0, responsible: '', budget: '', status: 'attivo', notes: '' });
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const openEditProject = (p) => {
    setEditProject(p);
    setProjectForm({
      name: p.name || '', description: p.description || '',
      start_date: p.start_date || '', end_date: p.end_date || '',
      progress_pct: p.progress_pct || 0, responsible: p.responsible || '',
      budget: p.budget?.toString() || '', status: p.status || 'attivo', notes: p.notes || ''
    });
    setShowProjectForm(true);
  };

  const handleDeleteProject = async (id) => {
    if (!window.confirm('Eliminare questo progetto?')) return;
    try { await api.delete('/projects/' + id); load(); } catch (err) { alert('Errore'); }
  };

  const handleDeleteMaterial = async (id) => {
    if (!window.confirm('Rimuovere questo materiale?')) return;
    try { await api.delete(`/site-planning/materials/${id}`); load(); } catch (err) { alert('Errore'); }
  };

  const openEditWork = (w) => {
    setEditWork(w);
    setWorkForm({
      name: w.name, description: w.description || '', mq_planned: w.mq_planned?.toString() || '',
      mq_completed: w.mq_completed?.toString() || '', status: w.status, start_date: w.start_date || '',
      end_date: w.end_date || '', notes: w.notes || ''
    });
    setShowWorkForm(true);
  };

  if (loading) return <div className="animate-pulse text-center py-20 text-slate-400">Caricamento...</div>;
  if (!data) return <div className="text-slate-500">Cantiere non trovato</div>;

  const site = data.site;
  const pct = data.completion_pct;
  const pctColor = pct >= 80 ? 'bg-green-500' : pct >= 40 ? 'bg-blue-500' : pct > 0 ? 'bg-amber-500' : 'bg-slate-300';

  return (
    <div className="space-y-6 animate-fade-in" data-testid="site-planning-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/sites')}><ArrowLeft size={18} /></Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900">{site.name}</h1>
          <p className="text-sm text-slate-500">{site.client} - {site.location}</p>
        </div>
        <Badge variant="outline" className={site.status === 'attivo' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-slate-100 text-slate-600'}>{site.status}</Badge>
      </div>

      {/* Progress Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4 text-center">
            <Ruler size={20} className="mx-auto text-slate-500 mb-1" />
            <p className="text-xs text-slate-500">MQ Totali</p>
            <div className="flex items-center gap-2 justify-center mt-1">
              {canEdit ? (
                <>
                  <Input type="number" className="w-24 text-center h-8 text-lg font-bold" value={mqEdit} onChange={(e) => setMqEdit(e.target.value)} />
                  <Button size="sm" variant="ghost" onClick={handleSaveMq} disabled={savingMq}><Save size={14} /></Button>
                </>
              ) : (
                <p className="text-2xl font-bold">{data.mq_total}</p>
              )}
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 text-center">
            <BarChart3 size={20} className="mx-auto text-blue-500 mb-1" />
            <p className="text-xs text-slate-500">MQ Eseguiti</p>
            <p className="text-2xl font-bold text-blue-600">{data.mq_completed}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 text-center">
            <div className="w-16 h-16 mx-auto mb-1 relative">
              <svg viewBox="0 0 36 36" className="w-full h-full">
                <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#e2e8f0" strokeWidth="3" />
                <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke={pct >= 80 ? '#22c55e' : pct >= 40 ? '#3b82f6' : '#f59e0b'} strokeWidth="3" strokeDasharray={`${pct}, 100`} />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-sm font-bold">{pct}%</span>
              </div>
            </div>
            <p className="text-xs text-slate-500">Completamento</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 text-center">
            <div className="grid grid-cols-2 gap-2 text-center">
              <div><p className="text-lg font-bold text-slate-700">{site.assigned_employees?.length || 0}</p><p className="text-xs text-slate-500">Operatori</p></div>
              <div><p className="text-lg font-bold text-slate-700">{site.assigned_equipment?.length || 0}</p><p className="text-xs text-slate-500">Attrezzature</p></div>
              <div><p className="text-lg font-bold text-slate-700">{data.works?.length || 0}</p><p className="text-xs text-slate-500">Lavorazioni</p></div>
              <div><p className="text-lg font-bold text-slate-700">{data.sal_count || 0}</p><p className="text-xs text-slate-500">SAL</p></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Progress bar */}
      <div>
        <div className="flex justify-between text-xs text-slate-500 mb-1">
          <span>Avanzamento cantiere</span>
          <span>{data.mq_completed} / {data.mq_total} MQ ({pct}%)</span>
        </div>
        <div className="w-full bg-slate-200 rounded-full h-3">
          <div className={`h-3 rounded-full transition-all ${pctColor}`} style={{ width: `${Math.min(100, pct)}%` }} />
        </div>
      </div>

      <Tabs defaultValue="progetti">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="progetti">Progetti ({projects.length})</TabsTrigger>
          <TabsTrigger value="lavorazioni">Lavorazioni ({data.works?.length || 0})</TabsTrigger>
          <TabsTrigger value="materiali">Materiali ({data.materials?.length || 0})</TabsTrigger>
        </TabsList>

        <TabsContent value="progetti">
          <div className="space-y-4 mt-4">
            {canEdit && (
              <Button onClick={() => { setEditProject(null); setProjectForm({ name: '', description: '', start_date: '', end_date: '', progress_pct: 0, responsible: '', budget: '', status: 'attivo', notes: '' }); setShowProjectForm(true); }}>
                <Plus size={16} className="mr-1" /> Nuovo Progetto
              </Button>
            )}
            {projects.length === 0 ? (
              <p className="text-slate-400 text-center py-8">Nessun progetto per questo cantiere</p>
            ) : (
              <div className="space-y-2">
                {projects.map(p => {
                  const pColor = p.status === 'completato' ? 'bg-emerald-500' : p.progress_pct >= 60 ? 'bg-blue-500' : p.progress_pct >= 30 ? 'bg-amber-500' : 'bg-slate-400';
                  const STATUS_LABELS = { attivo: 'Attivo', pianificato: 'Pianificato', sospeso: 'Sospeso', completato: 'Completato' };
                  const STATUS_CSS = { attivo: 'bg-green-100 text-green-700', pianificato: 'bg-blue-100 text-blue-700', sospeso: 'bg-amber-100 text-amber-700', completato: 'bg-emerald-100 text-emerald-700' };
                  return (
                    <Card key={p.id} className="border-slate-200">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <FolderOpen size={16} className="text-slate-500 flex-shrink-0" />
                              <span className="font-semibold text-sm">{p.name}</span>
                              <Badge className={STATUS_CSS[p.status] || ''}>{STATUS_LABELS[p.status] || p.status}</Badge>
                            </div>
                            {p.description && <p className="text-xs text-slate-500 mb-2 ml-6">{p.description}</p>}
                            <div className="flex flex-wrap gap-3 text-xs text-slate-500 ml-6">
                              {p.responsible && <span>Resp: <strong>{p.responsible}</strong></span>}
                              {p.start_date && <span>{fmtDate(p.start_date)}{p.end_date ? ' - ' + fmtDate(p.end_date) : ''}</span>}
                              {p.budget != null && <span>Budget: <strong>{p.budget.toLocaleString('it-IT')} EUR</strong></span>}
                            </div>
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <div className="text-right">
                              <div className="w-28 bg-slate-200 rounded-full h-2.5 mb-0.5">
                                <div className={"h-2.5 rounded-full transition-all " + pColor} style={{ width: Math.min(100, p.progress_pct) + '%' }} />
                              </div>
                              <span className="text-xs font-bold">{p.progress_pct}%</span>
                            </div>
                            {canEdit && (
                              <div className="flex gap-0.5">
                                <Button variant="ghost" size="sm" className="p-1 h-auto text-blue-600" onClick={() => openEditProject(p)}><Edit size={14} /></Button>
                                <Button variant="ghost" size="sm" className="p-1 h-auto text-red-600" onClick={() => handleDeleteProject(p.id)}><Trash2 size={14} /></Button>
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="lavorazioni">
          <div className="space-y-4 mt-4">
            {canEdit && (
              <Button onClick={() => { setEditWork(null); setWorkForm({ name: '', description: '', mq_planned: '', mq_completed: '', status: 'da_iniziare', start_date: '', end_date: '', notes: '' }); setShowWorkForm(true); }}>
                <Plus size={16} className="mr-1" /> Nuova Lavorazione
              </Button>
            )}
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-0">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Lavorazione</TableHead><TableHead className="text-center">MQ Prev.</TableHead>
                    <TableHead className="text-center">MQ Eseg.</TableHead><TableHead className="text-center">%</TableHead>
                    <TableHead>Stato</TableHead><TableHead className="hidden sm:table-cell">Periodo</TableHead>
                    {canEdit && <TableHead className="w-20">Azioni</TableHead>}
                  </TableRow></TableHeader>
                  <TableBody>
                    {(!data.works || data.works.length === 0) ? (
                      <TableRow><TableCell colSpan={7} className="text-center py-6 text-slate-500">Nessuna lavorazione</TableCell></TableRow>
                    ) : data.works.map(w => {
                      const wp = w.mq_planned > 0 ? Math.round(w.mq_completed / w.mq_planned * 100) : 0;
                      const ws = WORK_STATUSES[w.status] || WORK_STATUSES.da_iniziare;
                      return (
                        <TableRow key={w.id} className="hover:bg-slate-50">
                          <TableCell>
                            <div><p className="text-sm font-medium">{w.name}</p>{w.description && <p className="text-xs text-slate-500 truncate max-w-[200px]">{w.description}</p>}</div>
                          </TableCell>
                          <TableCell className="text-center font-medium">{w.mq_planned}</TableCell>
                          <TableCell className="text-center font-bold text-blue-600">{w.mq_completed}</TableCell>
                          <TableCell className="text-center">
                            <span className={`font-bold ${wp >= 80 ? 'text-green-600' : wp >= 40 ? 'text-blue-600' : 'text-amber-600'}`}>{wp}%</span>
                          </TableCell>
                          <TableCell><Badge className={ws.c}>{ws.l}</Badge></TableCell>
                          <TableCell className="hidden sm:table-cell text-xs text-slate-500">
                            {w.start_date && w.end_date ? `${fmtDate(w.start_date)} - ${fmtDate(w.end_date)}` : fmtDate(w.start_date) || '-'}
                          </TableCell>
                          {canEdit && (
                            <TableCell>
                              <div className="flex gap-1">
                                <Button variant="ghost" size="sm" onClick={() => openEditWork(w)} className="p-1 h-auto text-blue-600"><Edit size={14} /></Button>
                                <Button variant="ghost" size="sm" onClick={() => handleDeleteWork(w.id)} className="p-1 h-auto text-red-600"><Trash2 size={14} /></Button>
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="materiali">
          <div className="space-y-4 mt-4">
            {canEdit && (
              <Button onClick={() => setShowMaterialForm(true)}><Plus size={16} className="mr-1" /> Aggiungi Materiale</Button>
            )}
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-0">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Materiale</TableHead><TableHead className="text-center">Quantita</TableHead>
                    <TableHead>Unita</TableHead><TableHead>Note</TableHead>
                    {canEdit && <TableHead className="w-16">Azioni</TableHead>}
                  </TableRow></TableHeader>
                  <TableBody>
                    {(!data.materials || data.materials.length === 0) ? (
                      <TableRow><TableCell colSpan={5} className="text-center py-6 text-slate-500">Nessun materiale</TableCell></TableRow>
                    ) : data.materials.map(m => (
                      <TableRow key={m.id}>
                        <TableCell className="text-sm font-medium">{m.name}</TableCell>
                        <TableCell className="text-center font-medium">{m.quantity}</TableCell>
                        <TableCell className="text-sm">{m.unit}</TableCell>
                        <TableCell className="text-sm text-slate-500">{m.notes || '-'}</TableCell>
                        {canEdit && (
                          <TableCell>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteMaterial(m.id)} className="p-1 h-auto text-red-600"><Trash2 size={14} /></Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Work Dialog */}
      <Dialog open={showWorkForm} onOpenChange={setShowWorkForm}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editWork ? 'Modifica Lavorazione' : 'Nuova Lavorazione'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5"><Label>Nome *</Label><Input value={workForm.name} onChange={(e) => setWorkForm({ ...workForm, name: e.target.value })} /></div>
            <div className="col-span-2 space-y-1.5"><Label>Descrizione</Label><Textarea value={workForm.description} onChange={(e) => setWorkForm({ ...workForm, description: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>MQ Previsti</Label><Input type="number" step="0.1" value={workForm.mq_planned} onChange={(e) => setWorkForm({ ...workForm, mq_planned: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>MQ Eseguiti</Label><Input type="number" step="0.1" value={workForm.mq_completed} onChange={(e) => setWorkForm({ ...workForm, mq_completed: e.target.value })} /></div>
            <div className="space-y-1.5">
              <Label>Stato</Label>
              <Select value={workForm.status} onValueChange={(v) => setWorkForm({ ...workForm, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="da_iniziare">Da Iniziare</SelectItem>
                  <SelectItem value="in_corso">In Corso</SelectItem>
                  <SelectItem value="completata">Completata</SelectItem>
                  <SelectItem value="sospesa">Sospesa</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Data Inizio</Label><Input type="date" value={workForm.start_date} onChange={(e) => setWorkForm({ ...workForm, start_date: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Data Fine</Label><Input type="date" value={workForm.end_date} onChange={(e) => setWorkForm({ ...workForm, end_date: e.target.value })} /></div>
            <div className="col-span-2 space-y-1.5"><Label>Note</Label><Textarea value={workForm.notes} onChange={(e) => setWorkForm({ ...workForm, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWorkForm(false)}>Annulla</Button>
            <Button onClick={handleSaveWork} disabled={!workForm.name}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

            {/* Project Dialog */}
      <Dialog open={showProjectForm} onOpenChange={setShowProjectForm}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editProject ? 'Modifica Progetto' : 'Nuovo Progetto'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Nome Progetto *</Label><Input value={projectForm.name} onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })} placeholder="Es: Manutenzione tubazioni" /></div>
            <div className="space-y-1.5"><Label>Descrizione</Label><Textarea value={projectForm.description} onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Data Inizio</Label><Input type="date" value={projectForm.start_date} onChange={(e) => setProjectForm({ ...projectForm, start_date: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Data Fine Prevista</Label><Input type="date" value={projectForm.end_date} onChange={(e) => setProjectForm({ ...projectForm, end_date: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Responsabile</Label><Input value={projectForm.responsible} onChange={(e) => setProjectForm({ ...projectForm, responsible: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Budget (EUR)</Label><Input type="number" step="0.01" value={projectForm.budget} onChange={(e) => setProjectForm({ ...projectForm, budget: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5">
              <Label>Stato</Label>
              <Select value={projectForm.status} onValueChange={(v) => setProjectForm({ ...projectForm, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="attivo">Attivo</SelectItem>
                  <SelectItem value="pianificato">Pianificato</SelectItem>
                  <SelectItem value="sospeso">Sospeso</SelectItem>
                  <SelectItem value="completato">Completato</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between"><Label>Avanzamento</Label><span className="text-sm font-bold">{projectForm.progress_pct}%</span></div>
              <Slider value={[parseInt(projectForm.progress_pct) || 0]} onValueChange={(v) => setProjectForm({ ...projectForm, progress_pct: v[0] })} min={0} max={100} step={5} />
            </div>
            <div className="space-y-1.5"><Label>Note</Label><Textarea value={projectForm.notes} onChange={(e) => setProjectForm({ ...projectForm, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowProjectForm(false); setEditProject(null); }}>Annulla</Button>
            <Button onClick={handleSaveProject} disabled={!projectForm.name}>{editProject ? 'Salva Modifiche' : 'Crea Progetto'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Material Dialog */}
      <Dialog open={showMaterialForm} onOpenChange={setShowMaterialForm}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Aggiungi Materiale</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Nome *</Label><Input value={materialForm.name} onChange={(e) => setMaterialForm({ ...materialForm, name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Quantita</Label><Input type="number" step="0.1" value={materialForm.quantity} onChange={(e) => setMaterialForm({ ...materialForm, quantity: e.target.value })} /></div>
              <div className="space-y-1.5">
                <Label>Unita</Label>
                <Select value={materialForm.unit} onValueChange={(v) => setMaterialForm({ ...materialForm, unit: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pz">Pezzi</SelectItem>
                    <SelectItem value="mq">MQ</SelectItem>
                    <SelectItem value="ml">ML</SelectItem>
                    <SelectItem value="kg">Kg</SelectItem>
                    <SelectItem value="lt">Litri</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label>Note</Label><Input value={materialForm.notes} onChange={(e) => setMaterialForm({ ...materialForm, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMaterialForm(false)}>Annulla</Button>
            <Button onClick={handleAddMaterial} disabled={!materialForm.name}>Aggiungi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
